import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, ArrowRight, Search, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400&h=500&fit=crop',
  'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=400&h=500&fit=crop',
  'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=400&h=500&fit=crop',
];

export default function HeroSection({ section }) {
  const { title, subtitle, body, data = {} } = section;
  const ctaPrimary = data.cta_primary;
  const ctaSecondary = data.cta_secondary;
  const badgeText = data.badge_text;
  const heroImages = [0, 1, 2].map(i => (data.images || [])[i] || HERO_IMAGES[i]);

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}
      data-testid="section-hero"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            {subtitle && <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-4">{subtitle}</p>}
            {badgeText && <Badge variant="outline" className="mb-4 text-xs px-3 py-1">{badgeText}</Badge>}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-[-0.02em] text-ink mb-5 leading-tight">{title}</h1>
            {body && <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xl mb-8">{body}</p>}
            {(ctaPrimary || ctaSecondary) && (
              <div className="flex flex-wrap gap-3">
                {ctaPrimary && <Link to={ctaPrimary.href} data-testid="hero-cta-primary"><Button className="h-11 px-6 rounded-[12px] gap-2 shadow-[0_12px_26px_rgba(14,14,16,0.10)] active:scale-[0.98]">{ctaPrimary.label}<ArrowRight className="h-4 w-4" /></Button></Link>}
                {ctaSecondary && <Link to={ctaSecondary.href} data-testid="hero-cta-secondary"><Button variant="outline" className="h-11 px-6 rounded-[12px] gap-2 active:scale-[0.98]"><Search className="h-4 w-4" />{ctaSecondary.label}</Button></Link>}
              </div>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="hidden lg:flex gap-3 justify-end">
            <div className="flex flex-col gap-3">
              <div className="w-44 h-56 rounded-2xl overflow-hidden shadow-[var(--shadow-card)]">
                <img src={heroImages[0]} alt="Luxury handbag" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="w-44 h-36 rounded-2xl overflow-hidden shadow-[var(--shadow-card)]">
                <img src={heroImages[2]} alt="Designer shoes" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
            <div className="flex flex-col gap-3 mt-8">
              <div className="w-44 h-48 rounded-2xl overflow-hidden shadow-[var(--shadow-card)]">
                <img src={heroImages[1]} alt="Luxury watch" className="w-full h-full object-cover" loading="lazy" />
              </div>
              {/* Verified Authentic badge - HIGH VISIBILITY */}
              <div className="w-44 h-32 rounded-2xl overflow-hidden shadow-lg" style={{ background: 'linear-gradient(135deg, #0E6B5D 0%, #0a5449 100%)' }}>
                <div className="h-full flex flex-col items-center justify-center p-4">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center mb-2">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-white text-xs font-semibold uppercase tracking-wider">Verified</p>
                  <p className="text-emerald-200 text-[10px] font-medium uppercase tracking-widest">Authentic</p>
                  <div className="mt-1.5 h-px w-12 bg-white/30" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
